import React, { useState, useEffect } from "react";
import axios from "axios";

/* ─── Icons ──────────────────────────────────────────────────────────────── */
const MapPinIcon = ({ size = 20, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
    strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);
const PhoneIcon = ({ size = 20, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
    strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.32 2 2 0 0 1 3.6 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.64a16 16 0 0 0 6.29 6.29l.95-.95a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);
const ClockIcon = ({ size = 20, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
    strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
  </svg>
);
const MailIcon = ({ size = 20, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
    strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);
const SendIcon = ({ size = 18, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
    strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);

/* ─── Contact Component ──────────────────────────────────────────────────── */
const Contact = () => {
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [status, setStatus] = useState({ type: "", message: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ type: "", message: "" });
    try {
      const response = await axios.post("/api/contacts", formData);
      if (response.data.success) {
        setStatus({ type: "success", message: "Message sent! We'll get back to you soon. 🐾" });
        setFormData({ name: "", email: "", message: "" });
      }
    } catch (error) {
      setStatus({
        type: "error",
        message: error.response?.data?.message || "Failed to send message. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const contactDetails = [
    { Icon: MapPinIcon, label: "Address", value: "96 Gen. Luna St., Tuktukan, Taguig City", color: "#7C3AED" },
    { Icon: PhoneIcon, label: "Phone", value: "0954 498 8342", color: "#EC4899", href: "tel:09544988342" },
    { Icon: ClockIcon, label: "Hours", value: "Tue–Sun: 9 AM–7 PM\nMonday: Closed (Hotel check-out OK)", color: "#F97316" },
    { Icon: MailIcon, label: "Email", value: "furpecttails@gmail.com", color: "#14B8A6", href: "mailto:furpecttails@gmail.com" },
  ];

  return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(135deg, #faf5ff 0%, #fdf2f8 40%, #fff7ed 100%)" }}>
      {/* ── Header ── */}
      <section style={{ padding: "5rem 1.5rem 3rem", textAlign: "center", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: "-4rem", left: "-4rem", width: "20rem", height: "20rem", borderRadius: "50%", background: "radial-gradient(circle, rgba(168,85,247,0.12) 0%, transparent 70%)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", bottom: "0", right: "-3rem", width: "16rem", height: "16rem", borderRadius: "50%", background: "radial-gradient(circle, rgba(236,72,153,0.1) 0%, transparent 70%)", pointerEvents: "none" }} />
        <div style={{ position: "relative", zIndex: 1 }}>
          <span style={{ display: "inline-block", background: "rgba(124,58,237,0.1)", color: "#7C3AED", fontSize: "0.8rem", fontWeight: 800, padding: "0.4rem 1rem", borderRadius: "999px", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "1rem" }}>
            Get In Touch
          </span>
          <h1 style={{ fontSize: "clamp(2.2rem, 5vw, 3.2rem)", fontWeight: 900, color: "#1f2937", margin: "0 0 1rem", letterSpacing: "-0.02em" }}>
            We'd Love to{" "}
            <span style={{ background: "linear-gradient(90deg,#7C3AED,#EC4899)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
              Hear From You
            </span>
          </h1>
          <p style={{ fontSize: "1.1rem", color: "#6B7280", maxWidth: "500px", margin: "0 auto" }}>
            Have a question about our services? We're here to help your furry friend!
          </p>
        </div>
      </section>

      {/* ── Main Content ── */}
      <section style={{ maxWidth: "1200px", margin: "0 auto", padding: "0 1.5rem 6rem" }}>
        <div style={{
          display: "grid",
          gridTemplateColumns: "minmax(0,1fr) minmax(0,1.3fr)",
          gap: "2rem",
          alignItems: "start",
        }}
          className="contact-grid"
        >
          {/* Left Column — Info + Form */}
          <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
            {/* Contact info cards */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.9rem" }}>
              {contactDetails.map(({ Icon, label, value, color, href }) => (
                <div key={label} style={{
                  background: "white",
                  borderRadius: "1.2rem",
                  padding: "1.25rem",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
                  border: "1.5px solid rgba(0,0,0,0.05)",
                  transition: "transform 0.2s, box-shadow 0.2s",
                }}
                  onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 30px rgba(124,58,237,0.12)"; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,0,0,0.06)"; }}
                >
                  <div style={{ width: "2.4rem", height: "2.4rem", borderRadius: "0.8rem", background: `${color}18`, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "0.75rem" }}>
                    <Icon size={18} color={color} />
                  </div>
                  <p style={{ margin: "0 0 0.3rem", fontSize: "0.72rem", fontWeight: 800, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</p>
                  {href ? (
                    <a href={href} style={{ color: "#1f2937", fontSize: "0.88rem", fontWeight: 700, textDecoration: "none", whiteSpace: "pre-line" }}>{value}</a>
                  ) : (
                    <p style={{ margin: 0, color: "#1f2937", fontSize: "0.88rem", fontWeight: 600, whiteSpace: "pre-line" }}>{value}</p>
                  )}
                </div>
              ))}
            </div>

            {/* Contact Form */}
            <div style={{ background: "white", borderRadius: "1.5rem", padding: "2rem", boxShadow: "0 8px 40px rgba(124,58,237,0.1)", border: "1.5px solid rgba(124,58,237,0.08)" }}>
              <h2 style={{ fontWeight: 900, color: "#1f2937", fontSize: "1.3rem", margin: "0 0 1.5rem" }}>
                Send Us a Message
              </h2>

              {status.message && (
                <div style={{
                  marginBottom: "1.25rem", padding: "0.9rem 1.1rem", borderRadius: "0.9rem",
                  fontSize: "0.9rem", fontWeight: 600,
                  background: status.type === "success" ? "#F0FDF4" : "#FEF2F2",
                  color: status.type === "success" ? "#15803D" : "#DC2626",
                  border: `1px solid ${status.type === "success" ? "#BBF7D0" : "#FECACA"}`,
                }}>
                  {status.message}
                </div>
              )}

              <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                <input
                  id="contact-name"
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your Name"
                  required
                  style={{ width: "100%", padding: "0.85rem 1rem", borderRadius: "0.9rem", border: "2px solid rgba(124,58,237,0.15)", fontSize: "0.95rem", outline: "none", transition: "border-color 0.2s", boxSizing: "border-box", background: "#faf5ff" }}
                  onFocus={e => e.target.style.borderColor = "#7C3AED"}
                  onBlur={e => e.target.style.borderColor = "rgba(124,58,237,0.15)"}
                />
                <input
                  id="contact-email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Your Email"
                  required
                  style={{ width: "100%", padding: "0.85rem 1rem", borderRadius: "0.9rem", border: "2px solid rgba(124,58,237,0.15)", fontSize: "0.95rem", outline: "none", transition: "border-color 0.2s", boxSizing: "border-box", background: "#faf5ff" }}
                  onFocus={e => e.target.style.borderColor = "#7C3AED"}
                  onBlur={e => e.target.style.borderColor = "rgba(124,58,237,0.15)"}
                />
                <textarea
                  id="contact-message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Your Message"
                  rows="4"
                  required
                  style={{ width: "100%", padding: "0.85rem 1rem", borderRadius: "0.9rem", border: "2px solid rgba(124,58,237,0.15)", fontSize: "0.95rem", outline: "none", transition: "border-color 0.2s", boxSizing: "border-box", resize: "none", background: "#faf5ff" }}
                  onFocus={e => e.target.style.borderColor = "#7C3AED"}
                  onBlur={e => e.target.style.borderColor = "rgba(124,58,237,0.15)"}
                />
                <button
                  id="contact-submit-btn"
                  type="submit"
                  disabled={loading}
                  style={{
                    background: loading ? "#C4B5FD" : "linear-gradient(135deg,#7C3AED,#EC4899)",
                    color: "white",
                    padding: "0.95rem 1.5rem",
                    borderRadius: "0.9rem",
                    fontWeight: 800,
                    fontSize: "1rem",
                    border: "none",
                    cursor: loading ? "not-allowed" : "pointer",
                    boxShadow: loading ? "none" : "0 8px 25px rgba(124,58,237,0.35)",
                    transition: "all 0.3s",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "0.5rem",
                  }}
                  onMouseEnter={e => { if (!loading) { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 12px 35px rgba(124,58,237,0.45)"; }}}
                  onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = loading ? "none" : "0 8px 25px rgba(124,58,237,0.35)"; }}
                >
                  {loading ? "Sending..." : <><SendIcon size={16} color="white" /> Send Message</>}
                </button>
              </form>
            </div>
          </div>

          {/* Right Column — Google Maps */}
          <div style={{ position: "sticky", top: "2rem" }}>
            <div style={{
              borderRadius: "1.5rem",
              overflow: "hidden",
              boxShadow: "0 20px 60px rgba(124,58,237,0.15)",
              border: "1.5px solid rgba(124,58,237,0.12)",
            }}>
              {/* Map header */}
              <div style={{
                background: "linear-gradient(135deg,#7C3AED,#EC4899)",
                padding: "1.25rem 1.5rem",
                display: "flex",
                alignItems: "center",
                gap: "0.75rem",
              }}>
                <MapPinIcon size={20} color="white" />
                <div>
                  <p style={{ margin: 0, fontWeight: 900, color: "white", fontSize: "1rem" }}>Furpect Tails</p>
                  <p style={{ margin: 0, color: "rgba(255,255,255,0.75)", fontSize: "0.8rem" }}>96 Gen. Luna St., Tuktukan, Taguig City</p>
                </div>
                <a
                  href="https://www.google.com/maps/search/96+Gen.+Luna+St+Tuktukan+Taguig+City"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    marginLeft: "auto",
                    background: "rgba(255,255,255,0.2)",
                    color: "white",
                    padding: "0.4rem 0.9rem",
                    borderRadius: "999px",
                    fontSize: "0.78rem",
                    fontWeight: 700,
                    textDecoration: "none",
                    border: "1px solid rgba(255,255,255,0.3)",
                    whiteSpace: "nowrap",
                    transition: "background 0.2s",
                  }}
                  onMouseEnter={e => e.target.style.background = "rgba(255,255,255,0.3)"}
                  onMouseLeave={e => e.target.style.background = "rgba(255,255,255,0.2)"}
                >
                  Open in Maps ↗
                </a>
              </div>

              {/* Google Maps iframe */}
              <iframe
                id="contact-map"
                title="Furpect Tails Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3862.2!2d121.047!3d14.521!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3397c9f0e3b8ef3f%3A0x1!2s96+Gen.+Luna+St%2C+Tuktukan%2C+Taguig%2C+Metro+Manila!5e0!3m2!1sen!2sph!4v1690000000000!5m2!1sen!2sph"
                width="100%"
                height="420"
                style={{ border: "none", display: "block" }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>

            {/* Quick directions card */}
            <div style={{ marginTop: "1rem", background: "white", borderRadius: "1.2rem", padding: "1.25rem 1.5rem", boxShadow: "0 4px 20px rgba(0,0,0,0.06)", border: "1.5px solid rgba(0,0,0,0.05)" }}>
              <p style={{ margin: "0 0 0.75rem", fontWeight: 800, color: "#1f2937", fontSize: "0.9rem" }}>🚌 Getting Here</p>
              <ul style={{ margin: 0, padding: "0 0 0 1.2rem", color: "#6B7280", fontSize: "0.85rem", lineHeight: 1.8 }}>
                <li>Jeepney routes passing Tuktukan, Taguig</li>
                <li>Near Taguig City Hall area</li>
                <li>Landmark: Gen. Luna Street</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        @media (max-width: 768px) {
          .contact-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
};

export default Contact;
